import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import gsap from 'gsap';
import { useConfigLoader } from '@/contexts/ConfigLoaderContext';

interface SplashScreenProps {
  children: React.ReactNode;
}

/**
 * SplashScreen Component
 *
 * Unified loading screen that orchestrates ALL configuration loading.
 * Dark arcade theme with GSAP animations matching ConnectionError design.
 */
export function SplashScreen({ children }: SplashScreenProps) {
  const { t } = useTranslation('common');
  const { stage, isReady, stageMessage } = useConfigLoader();
  const [progress, setProgress] = useState(0);
  const [showChildren, setShowChildren] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const dotsRef = useRef<HTMLDivElement>(null);
  const progressFillRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLParagraphElement>(null);
  const childrenRef = useRef<HTMLDivElement>(null);

  // Stage progress mapping
  const stageProgress: Record<string, number> = {
    'initializing': 0,
    'checking-connection': 20,
    'hydrating-store': 40,
    'loading-settings': 60,
    'applying-theme': 80,
    'ready': 100,
    'error': 100,
  };

  // Smooth progress animation
  useEffect(() => {
    const targetProgress = stageProgress[stage] ?? 0;
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= targetProgress) {
          clearInterval(interval);
          return targetProgress;
        }
        return prev + 2;
      });
    }, 50);
    return () => clearInterval(interval);
  }, [stage]);

  // GSAP entrance animations
  useEffect(() => {
    const ctx = gsap.context(() => {
      // Title entrance
      gsap.from(titleRef.current, {
        y: 30,
        opacity: 0,
        duration: 0.6,
        ease: 'power3.out',
      });

      // Subtitle entrance (staggered)
      gsap.from(subtitleRef.current, {
        y: 20,
        opacity: 0,
        duration: 0.5,
        delay: 0.3,
        ease: 'power3.out',
      });

      // Bouncing dots
      if (dotsRef.current) {
        const dots = dotsRef.current.children;
        gsap.to(dots, {
          y: -12,
          duration: 0.4,
          ease: 'power2.out',
          stagger: { each: 0.15, repeat: -1, yoyo: true },
        });
      }
    }, containerRef);

    return () => ctx.revert();
  }, []);

  // Animate progress bar fill with GSAP
  useEffect(() => {
    if (progressFillRef.current) {
      gsap.to(progressFillRef.current, {
        width: `${progress}%`,
        duration: 0.3,
        ease: 'power2.out',
      });
    }
  }, [progress]);

  // Animate stage message change
  useEffect(() => {
    if (stageRef.current) {
      gsap.fromTo(
        stageRef.current,
        { opacity: 0, y: 8 },
        { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' },
      );
    }
  }, [stageMessage]);

  // Fade out splash, then show children
  useEffect(() => {
    if (isReady && containerRef.current) {
      gsap.to(containerRef.current, {
        opacity: 0,
        duration: 0.4,
        ease: 'power2.in',
        onComplete: () => setShowChildren(true),
      });
    }
  }, [isReady]);

  // Fade in children
  useEffect(() => {
    if (showChildren && childrenRef.current) {
      gsap.fromTo(
        childrenRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.5, ease: 'power2.out' },
      );
    }
  }, [showChildren]);

  if (showChildren) {
    return <div ref={childrenRef}>{children}</div>;
  }

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 flex flex-col items-center justify-center z-50"
      style={{ background: '#0D0D1A' }}
    >
      {/* Title */}
      <h1
        ref={titleRef}
        className="text-5xl font-extrabold text-white mb-2 tracking-tight"
      >
        {t('splash.title')}
      </h1>

      {/* Subtitle */}
      <p
        ref={subtitleRef}
        className="text-lg mb-12"
        style={{ color: 'rgba(255,255,255,0.5)' }}
      >
        {t('splash.subtitle')}
      </p>

      {/* Bouncing Dots */}
      <div ref={dotsRef} className="flex gap-3 mb-8">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="w-3 h-3 rounded-full"
            style={{ background: '#F4D35E' }}
          />
        ))}
      </div>

      {/* Progress Bar */}
      <div className="w-64 mb-6">
        <div
          className="h-2 rounded-full overflow-hidden"
          style={{ background: 'rgba(255,255,255,0.1)' }}
        >
          <div
            ref={progressFillRef}
            className="h-full rounded-full"
            style={{ background: '#F4D35E', width: '0%' }}
          />
        </div>
      </div>

      {/* Stage Message */}
      <p
        ref={stageRef}
        className="text-sm"
        style={{ color: 'rgba(255,255,255,0.5)' }}
      >
        {stageMessage}
      </p>
    </div>
  );
}
